<template>
  <div class="rb-ob-form rb-match-page rb-book-page rb-pay-page rb-consent-page">
    <header><h1 class="rb-ob-title">{{ $t('intake.boundary03Title') }}</h1></header>
    <p class="rb-consent-privacy">{{ $t('intake.boundary03Privacy') }}</p>
    <p><router-link :to="legalRoute('private-letter')" target="_blank" rel="noopener">{{ isFa ? 'شرایط نامهٔ خصوصی برای خود مراجع' : 'Private-letter policy (Persian)' }}</router-link></p>

    <div class="rb-ob-status-panel">
      <p>{{ offerText }}</p>
    </div>

    <template v-if="captureReady">
      <label class="rb-ready-q" for="intakePrivateLetterText">
        <span class="rb-book-fee-label">{{ isFa ? 'نامه‌ای برای خود آینده‌تان (اختیاری)' : 'A private letter to your future self (optional)' }}</span>
      </label>
      <textarea
        id="intakePrivateLetterText"
        v-model.trim="letterText"
        class="rb-letter-textarea"
        rows="8"
        maxlength="20000"
        :disabled="submitting"
        :placeholder="isFa ? 'نوشتن این نامه کاملاً اختیاری است…' : 'Writing this letter is completely optional…'"
      />
      <p class="rb-ready-hint">
        {{ isFa
          ? 'پس از ثبت، ویرایش نامه انجام نمی‌شود. متن در حساب خودت قابل مشاهده است و برای دریافت پاسخ به درمانگر ارسال نمی‌شود. شرایط نگهداری و حذف را پیش از ثبت بخوان.'
          : 'Recorded text cannot be edited. You can read it in your account; it is not sent to your therapist for a reply. Read the storage and deletion policy before recording.' }}
      </p>
      <label class="rb-consent-check">
        <input v-model="letterAcknowledged" type="checkbox" :disabled="submitting">
        <span>{{ letterAcknowledgment }}</span>
      </label>
    </template>

    <div class="rb-ob-actions">
      <button
        v-if="captureReady"
        class="rb-ob-submit"
        type="button"
        :disabled="submitting || !care || !letterText || !letterAcknowledged"
        @click="saveText"
      >
        {{ submitting ? $t('intake.portal.loading') : (isFa ? 'ثبت و قفل کردن نامه' : 'Record and lock letter') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="submitting || !care" @click="skip">
        {{ $t('intake.boundary03Skip') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="goHome">{{ $t('intake.clientHomeCta') }}</button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
      <div class="rb-ob-trust"><p>{{ $t('intake.trustPath') }}</p><p>{{ $t('authUi.familyNote') }}</p></div>
    </div>
  </div>
</template>

<script>
import {clientMessage, clientError} from '@/intake/clientMessages'
import {getLegalDocument} from '@/legal/documents'
import {
  fetchActiveCare,
  fetchPrivateLetter,
  recordPrivateLetter,
  skipCarePrivateLetterOffer,
} from '@/intake/portalApi'

export default {
  name: 'MatchLetterStep',
  data() {
    return {
      care: null,
      capture: null,
      letterText: '',
      letterAcknowledged: false,
      formStatus: '',
      submitting: false,
    }
  },
  computed: {
    isFa() {
      return (this.$route.params.lang || 'fa') === 'fa'
    },
    letterAcknowledgment() {
      return this.isFa
        ? getLegalDocument('private-letter').acknowledgment
        : 'I understand that this letter is for me, cannot be edited after recording, and can be read in my account. I have read the storage and deletion policy and want to record and lock it.'
    },
    offerState() {
      return this.care && this.care.private_letter_offer ? this.care.private_letter_offer.state : null
    },
    captureReady() {
      return !!(
        this.offerState === 'OFFERED'
        && this.capture
        && this.capture.eligible
        && Array.isArray(this.capture.allowed_media)
        && this.capture.allowed_media.indexOf('TEXT') !== -1
        && this.capture.capture_copy_version
      )
    },
    offerText() {
      if (this.offerState === 'POLICY_UNAVAILABLE') {
        return this.isFa
          ? 'ثبت نامه خصوصی فعلاً در دسترس نیست. می‌توانی ادامهٔ مسیر را بدون ثبت نامه انجام بدهی.'
          : 'Private-letter capture is currently unavailable. You can continue without recording a letter.'
      }
      if (this.offerState === 'SKIPPED') {
        return this.isFa
          ? 'پیشنهاد نامه خصوصی را رد کردید. این کار روی آمادگی جلسه اثر ندارد.'
          : 'You skipped the optional private-letter offer. This does not affect care readiness.'
      }
      if (this.offerState === 'COMPLETED') {
        return this.isFa
          ? 'نامه خصوصی ثبت و قفل شد.'
          : 'Your private letter was recorded and locked.'
      }
      if (this.captureReady) {
        return this.isFa
          ? 'نامه خصوصی اختیاری است و شرط تطبیق، رضایت یا ورود به جلسه نیست. می‌توانی نامه را بنویسی؛ ضبط صوت فعلاً در دسترس نیست.'
          : 'The private letter is optional and is not required for matching, consent, or session entry. You can write your letter; voice recording is currently unavailable.'
      }
      return this.isFa
        ? 'نامه خصوصی اختیاری است و شرط تطبیق، رضایت یا ورود به جلسه نیست.'
        : 'The private letter is optional and is not a condition for matching, consent, or session entry.'
    },
  },
  mounted() {
    fetchActiveCare().then((care) => {
      this.care = care
      if (!care) {
        this.formStatus = clientMessage('CARE_CONTEXT_REQUIRED', this.isFa ? 'fa' : 'en')
        return null
      }
      if (care.private_letter_offer && care.private_letter_offer.available) {
        return fetchPrivateLetter(care.id).then((model) => {
          this.capture = (model && model.capture_eligible) || null
        })
      }
      return null
    }).catch((e) => {
      this.formStatus = clientError(e, this.isFa ? 'fa' : 'en')
    })
  },
  methods: {
    legalRoute(documentId) {
      return {name: 'legalDocument', params: {lang: this.$route.params.lang || 'fa', documentId}}
    },
    saveText() {
      if (!this.care || !this.captureReady || !this.letterText || !this.letterAcknowledged || this.submitting) {
        return
      }
      this.submitting = true
      this.formStatus = ''
      return recordPrivateLetter(this.care.id, {
        medium: 'TEXT',
        text: this.letterText,
        capture_copy_version: this.capture.capture_copy_version,
        idempotency_key: 'letter:' + this.care.id + ':TEXT',
      }).then(() => {
        if (this.care.private_letter_offer) {
          this.care.private_letter_offer.state = 'COMPLETED'
          this.care.private_letter_offer.available = false
        }
        this.goHome()
      }).catch((e) => {
        this.formStatus = clientError(e, this.isFa ? 'fa' : 'en')
      }).finally(() => {
        this.submitting = false
      })
    },
    skip() {
      if (!this.care) {
        return
      }
      this.submitting = true
      this.formStatus = ''
      skipCarePrivateLetterOffer(this.care.id)
        .then(() => {
          if (this.care.private_letter_offer) {
            this.care.private_letter_offer.state = 'SKIPPED'
          }
          this.goHome()
        })
        .catch((e) => {
          this.formStatus = clientError(e, this.isFa ? 'fa' : 'en')
        })
        .finally(() => {
          this.submitting = false
        })
    },
    goHome() {
      this.$router.push({name: 'portalHome', params: {lang: this.$route.params.lang || 'fa'}, query: this.$route.query})
    },
  },
}
</script>

<style scoped>
.rb-letter-textarea {
  width: 100%;
  margin: 12px 0;
  padding: 12px;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.16);
  border-radius: 8px;
  font: inherit;
  line-height: 1.7;
  resize: vertical;
  min-height: 160px;
}
</style>
